import { useNavigate } from 'react-router-dom';
import { impactOccuer } from '../utils/hapticEvents';
import { useRef, useState, useEffect, forwardRef, type ReactNode } from 'react';

import { cn } from '../lib/utils';

import { NewHomeIcon } from '../assets/footer/NewHome';
import { NewOrdersIcon } from '../assets/footer/NewOrders';
import { NewCartIcon } from '../assets/footer/NewCart';
import { NewActionIcon } from '../assets/footer/NewAction';
import { NewMoreIcon } from '../assets/footer/NewMore';
import { useSelector } from 'react-redux';
import { selectTotalQuantity } from '../store/selectors/basket';
import { useTranslation } from 'react-i18next';
import { TFunction } from 'i18next';
import { useGetActiveOrdersCount } from '../modules/orders/services/getActiveOrdersCount';

type CellProps = {
  id: number;
  Icon: React.FC<{ className?: string }>;
  basketCount?: number;
  text: string;
  href: string;
};

const cells = (t: TFunction): CellProps[] => [
  {
    id: 1,
    Icon: NewHomeIcon,
    text: t('menu'),
    href: '/',
  },
  {
    id: 2,
    Icon: NewOrdersIcon,
    text: t('orders'),
    href: '/profile/orders',
  },
  {
    id: 4,
    Icon: NewCartIcon,
    text: t('basket'),
    href: '/cart',
  },
  {
    id: 5,
    Icon: NewActionIcon,
    text: t('promotion'),
    href: '/promotions',
  },
  {
    id: 3,
    Icon: NewMoreIcon,
    text: t('more'),
    href: '/profile',
  },
];

type FooterSectionProps = {
  topSlot?: ReactNode;
};

export const FooterSection = forwardRef<HTMLElement, FooterSectionProps>(({ topSlot }, ref) => {
  const basketCount = useSelector(selectTotalQuantity);
  const activeOrdersCount = useGetActiveOrdersCount();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const itemRefs = useRef<(HTMLDivElement | null)[]>([]);
  const navRef = useRef<HTMLElement>(null);
  const [indicatorStyle, setIndicatorStyle] = useState<{
    left: number;
    width: number;
    opacity: number;
  }>({ left: 0, width: 0, opacity: 0 });

  const cellsList = cells(t);

  const activeIndex = cellsList.findIndex(({ href }) => {
    if (href === '/')
      return (
        /^\/(\d+)?$/.test(window.location.pathname) ||
        window.location.pathname === '/notification' ||
        window.location.pathname === '/search'
      );
    if (href === '/profile')
      return (
        window.location.pathname.startsWith('/profile') &&
        !window.location.pathname.startsWith('/profile/orders')
      );
    return window.location.pathname.startsWith(href);
  });

  useEffect(() => {
    if (activeIndex === -1 || !itemRefs.current[activeIndex] || !navRef.current) {
      setIndicatorStyle(prev => ({ ...prev, opacity: 0 }));
      return;
    }
    const item = itemRefs.current[activeIndex]!;
    setIndicatorStyle({
      left: item.offsetLeft,
      width: item.offsetWidth,
      opacity: 1,
    });
  }, [activeIndex]);

  return (
    <footer
      ref={ref}
      className="fixed bottom-0 left-0 right-0 z-40 flex flex-col"
      style={{
        transform: 'translateZ(0)',
        WebkitTransform: 'translateZ(0)',
        isolation: 'isolate',
      }}
    >
      {/* Layer 1: Backdrop blur — less at top, medium in middle, most at bottom */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          backdropFilter: 'blur(3px)',
          WebkitBackdropFilter: 'blur(3px)',
          maskImage: 'linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0.15) 15%, rgba(0,0,0,0.3) 40%, rgba(0,0,0,0.3) 60%, rgba(0,0,0,0) 100%)',
          WebkitMaskImage: 'linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0.15) 15%, rgba(0,0,0,0.3) 40%, rgba(0,0,0,0.3) 60%, rgba(0,0,0,0) 100%)',
        }}
      />
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          backdropFilter: 'blur(12px)',
          WebkitBackdropFilter: 'blur(12px)',
          maskImage: 'linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0.2) 20%, rgba(0,0,0,0.6) 40%, rgba(0,0,0,1) 55%, rgba(0,0,0,0) 100%)',
          WebkitMaskImage: 'linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0.2) 20%, rgba(0,0,0,0.6) 40%, rgba(0,0,0,1) 55%, rgba(0,0,0,0) 100%)',
        }}
      />
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
          maskImage: 'linear-gradient(180deg, rgba(0,0,0,0) 60%, rgba(0,0,0,1) 100%)',
          WebkitMaskImage: 'linear-gradient(180deg, rgba(0,0,0,0) 60%, rgba(0,0,0,1) 100%)',
        }}
      />
      {/* Layer 2: Vertical gradient — transparent top, milky bottom */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            'linear-gradient(180deg, rgba(240,240,240,0) 0%, rgba(240,240,240,0.5) 50%, rgba(240,240,240,0.9) 100%)',
        }}
      />

      {/* The panel treatment — rounded top and hairline — belongs to the slot
          layout only. Without a slot the footer is the bare floating capsule
          it has always been. Spacing down to the nav is the slot's own bottom
          padding, not a gap here, so each slot can set its own. */}
      <div
        className={cn(
          'relative flex w-full flex-col',
          topSlot && 'overflow-hidden rounded-t-[14px]'
        )}
      >
        {/* Deliberately unstyled: some slots want a solid full-bleed strip (the
            basket total), others want to float like the nav capsule (the
            replace-item bar). Each consumer brings its own backdrop. */}
        {topSlot && <div className="w-full">{topSlot}</div>}
        {/* With a slot above it, the nav shares the slot's solid backdrop so
            the two read as one band — the milky gradient behind the bare nav
            would otherwise show a seam right under the card. */}
        <div
          className={cn(
            'flex flex-row justify-center items-start w-full',
            topSlot && 'bg-fill-background'
          )}
          style={{ padding: topSlot ? '0 28px 28px' : '16px 28px 28px' }}
        >
          <nav
          ref={navRef}
          className="relative flex flex-row justify-between items-start h-[54px] w-full rounded-[100px]"
          style={{ isolation: 'isolate' }}
        >
          {/* Capsule Layer 1: Backdrop blur */}
          <div
            className="absolute pointer-events-none rounded-[100px]"
            style={{
              inset: -4,
              backdropFilter: 'blur(10px)',
              WebkitBackdropFilter: 'blur(10px)',
              zIndex: 0,
            }}
          />
          {/* Capsule Layer 2: Light tint — not white, subtle. Over a filled
              slot the band behind is already #F0F0F0, so that tint would be
              grey on grey and the capsule would vanish; there it goes white
              so the nav still reads as its own surface. */}
          <div
            className="absolute pointer-events-none rounded-[100px]"
            style={{
              inset: -4,
              background: topSlot
                ? 'rgba(255,255,255,0.9)'
                : 'rgba(240,240,240,0.6)',
              zIndex: 1,
            }}
          />
          {/* Capsule Layer 3: Vertical gradient for depth */}
          <div
            className="absolute pointer-events-none rounded-[100px]"
            style={{
              inset: -4,
              background:
                'linear-gradient(180deg, rgba(255,255,255,0.04) 0%, rgba(255,255,255,0.15) 100%)',
              zIndex: 2,
            }}
          />
          {/* Capsule Layer 4: Thin white border */}
          <div
            className="absolute pointer-events-none rounded-[100px]"
            style={{
              inset: -4,
              border: '0.5px solid rgba(255,255,255,0.25)',
              zIndex: 3,
            }}
          />
          {/* Capsule Layer 5: Inner top highlight */}
          <div
            className="absolute pointer-events-none rounded-[100px]"
            style={{
              inset: -4,
              background: 'linear-gradient(180deg, rgba(255,255,255,0.1) 0%, transparent 30%)',
              zIndex: 4,
            }}
          />
          {/* Capsule Layer 6: Soft outer shadow. Dropped while a slot is open —
              the capsule is sitting on that slot's solid backdrop rather than
              floating over the page, and a shadow there just muddies the band. */}
          {!topSlot && (
            <div
              className="absolute pointer-events-none rounded-[100px]"
              style={{
                inset: -4,
                boxShadow: '0 8px 32px rgba(0,0,0,0.06)',
                zIndex: 5,
              }}
            />
          )}

          {/* Selection indicator — subtle glass overlay */}
          <div
            className="absolute top-0 bottom-0 rounded-[100px] pointer-events-none transition-all duration-300 ease-out"
            style={{
              left: indicatorStyle.left,
              width: indicatorStyle.width,
              opacity: indicatorStyle.opacity,
              background: 'rgba(255,255,255,0.35)',
              zIndex: 6,
            }}
          />

          {/* Tab items */}
          {cellsList.map(({ id, text, Icon, href }, index) => {
            const selected = index === activeIndex;

            return (
              <div
                key={id}
                ref={el => { itemRefs.current[index] = el; }}
                onClick={() => {
                  // Tapping the tab is an explicit "take me to the top"
                  // request, so drop the restore targets the home screen would
                  // otherwise honour (saved scroll, pending category anchor).
                  if (href === '/') {
                    sessionStorage.removeItem('homeScrollPosition');
                    sessionStorage.removeItem('scrollToCategory');
                    document
                      .getElementById('scrollable')
                      ?.scrollTo({ top: 0, behavior: 'auto' });
                  }

                  navigate(href);
                  impactOccuer('rigid');
                }}
                className={cn(
                  'flex flex-col items-center justify-center gap-[2px] rounded-[100px] transition-colors duration-300 cursor-pointer relative',
                  'w-[68px] h-[54px] pt-[6px] pr-[8px] pb-[7px] pl-[8px]'
                )}
                style={{ zIndex: index + 7 }}
              >
                <div className="relative">
                  <Icon
                    className={cn(
                      'transition-colors duration-300',
                      selected ? 'text-[#2BB747]' : 'text-[#6B717D]'
                    )}
                  />
                  {id === 4 && basketCount ? (
                    <div className="min-w-[14px] h-[14px] px-[3px] rounded-full flex items-center justify-center text-text-white border border-white bg-fill-danger absolute -top-0.5 -right-1 text-[8px] font-semibold leading-none">
                      {basketCount}
                    </div>
                  ) : null}
                  {id === 2 && activeOrdersCount > 0 ? (
                    <div className="min-w-[14px] h-[14px] px-[3px] rounded-full flex items-center justify-center text-text-white border border-white bg-fill-danger absolute -top-0.5 -right-1 text-[8px] font-semibold leading-none">
                      {activeOrdersCount}
                    </div>
                  ) : null}
                </div>
                <span
                  className={cn(
                    'text-[10px] font-inter transition-colors duration-300',
                    selected ? 'text-[#2BB747]' : 'text-[#6B717D]'
                  )}
                >
                  {text}
                </span>
              </div>
            );
          })}
          </nav>
        </div>

        {/* 1px hairline that starts white at the top and fades out downwards.
            Drawn as a masked gradient ring rather than `border-image`, which
            ignores border-radius and would square off the rounded top corners.
            Same trick as the quantity pill in FoodDetail. */}
        {topSlot && (
          <div
            aria-hidden
            className="pointer-events-none absolute inset-0 rounded-t-[14px]"
            style={{
              padding: 1,
              background:
                'linear-gradient(180deg, #FFFFFF 0%, rgba(255,255,255,0) 100%)',
              WebkitMask:
                'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
              WebkitMaskComposite: 'xor',
              maskComposite: 'exclude',
            }}
          />
        )}
      </div>
    </footer>
  );
});

FooterSection.displayName = 'FooterSection';
